import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { invalidateSupportSessionQueries } from '@/custolink-hooks/useRefreshOnCallEnded'
import { Button } from '@/custolink-newdesign/ui/button'
import { Label } from '@/custolink-newdesign/ui/label'
import { Textarea } from '@/custolink-newdesign/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/custolink-newdesign/ui/dialog'
import { AdmSupportVideoCallSessionApi } from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'

/**
 * Skippable three-question end-of-call debrief (D-09): cause, fix, and what the next technician
 * should know. Always shown after the session has already ended - in the room after End session
 * (RemoteSupportRoomPage's renderAfterEndSession), and in the Support Hub live panel after Close
 * or End anyway (IncomingCalls' promptCallId) - so it can never block or undo a close. Skip,
 * Escape or closing the dialog call onDone with no request at all; the dialog never ends or
 * re-closes the session itself, and never reopens one (T-04-48).
 *
 * Several paths can prompt for the same session (End session in the room, Close or End anyway in
 * the Support Hub, a Close on a session that had already ended). The stored debrief is therefore
 * loaded first and pre-filled, with who answered it and when, so a second prompt edits the
 * existing answers instead of silently replacing them with blank ones. The fields stay disabled
 * until it has loaded.
 */
export function EndOfCallPromptDialog(p: { orgId: number; callId: number; onDone: () => void }) {
  const [cause, setCause] = useState('')
  const [fix, setFix] = useState('')
  const [nextTechnicianNote, setNextTechnicianNote] = useState('')
  const [loaded, setLoaded] = useState(false)

  const onDone = p.onDone
  const queryClient = useQueryClient()

  const existing = useQuery({
    queryKey: [
      'AdmSupportVideoCallSessionApi',
      AdmSupportVideoCallSessionApi.getDebrief.name,
      p.orgId,
      p.callId,
    ],
    queryFn: () => AdmSupportVideoCallSessionApi.getDebrief(p.orgId, p.callId),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (existing.data != null && !loaded) {
      setCause(existing.data.cause)
      setFix(existing.data.fix)
      setNextTechnicianNote(existing.data.nextTechnicianNote)
      setLoaded(true)
    }
    // Only ever initialise once, from the first successful load, so a refetch never overwrites
    // what the worker is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data])

  const saveDebrief = useMutation({
    mutationFn: () =>
      AdmSupportVideoCallSessionApi.saveDebrief(p.orgId, p.callId, {
        cause,
        fix,
        nextTechnicianNote,
      }),
    onSuccess: () => {
      // The Support Hub list and details drawer in this document (the IncomingCalls path).
      invalidateSupportSessionQueries(queryClient)
      onDone()
    },
  })

  const allBlank = cause.trim() === '' && fix.trim() === '' && nextTechnicianNote.trim() === ''
  const unchanged =
    existing.data != null &&
    cause === existing.data.cause &&
    fix === existing.data.fix &&
    nextTechnicianNote === existing.data.nextTechnicianNote
  const answeredBefore = existing.data?.answered === true ? existing.data : null

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onDone()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Before you go</DialogTitle>
          <DialogDescription>
            The session has ended. A few words help the next technician. You can skip this.
          </DialogDescription>
        </DialogHeader>

        {answeredBefore != null && (
          <p className="cl:text-sm cl:text-muted-foreground">
            Already answered
            {answeredBefore.authorDisplayName !== '' ? ` by ${answeredBefore.authorDisplayName}` : ''}
            {answeredBefore.answeredPretty !== '' ? ` on ${answeredBefore.answeredPretty}` : ''}.
            Saving replaces these answers.
          </p>
        )}

        {existing.isError && (
          <div className="cl:flex cl:items-center cl:gap-2">
            <p role="alert" className="cl:text-sm cl:text-destructive">
              Could not load the existing answers.
            </p>
            <Button type="button" variant="outline" size="sm" onClick={() => existing.refetch()}>
              Try again
            </Button>
          </div>
        )}

        <div className="cl:flex cl:flex-col cl:gap-4">
          <div className="cl:flex cl:flex-col cl:gap-1.5">
            <Label htmlFor="debrief-cause">What caused the problem?</Label>
            <Textarea
              id="debrief-cause"
              maxLength={4000}
              disabled={!loaded}
              value={cause}
              onChange={(e) => setCause(e.target.value)}
            />
          </div>

          <div className="cl:flex cl:flex-col cl:gap-1.5">
            <Label htmlFor="debrief-fix">What fixed it?</Label>
            <Textarea
              id="debrief-fix"
              maxLength={4000}
              disabled={!loaded}
              value={fix}
              onChange={(e) => setFix(e.target.value)}
            />
          </div>

          <div className="cl:flex cl:flex-col cl:gap-1.5">
            <Label htmlFor="debrief-next">What should the next technician know?</Label>
            <Textarea
              id="debrief-next"
              maxLength={4000}
              disabled={!loaded}
              value={nextTechnicianNote}
              onChange={(e) => setNextTechnicianNote(e.target.value)}
            />
          </div>

          {saveDebrief.isError && (
            <p role="alert" className="cl:text-sm cl:text-destructive">
              Could not save. Try again or skip.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            Skip
          </Button>
          <Button
            type="button"
            disabled={!loaded || saveDebrief.isPending || allBlank || unchanged}
            onClick={() => saveDebrief.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EndOfCallPromptDialog
